import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { request } from 'graphql-request';
import useGraphqlRequestParams from '@elementary/common-ui/hooks/useGraphqlRequestParams';
import { UrlFilters } from '@elementary/common-ui/hooks/useUrlFilters';
import { INCIDENT_LIST_GROUP_SUMMARY } from '@elementary/common-ui/constants/queryKeys';
import { getIncidentsSummaryQuery } from '../api';
import type { IncidentListBaseFilters, IncidentListSummary } from '../types';

type Args = {
  field: string | undefined;
  groupValues: string[];
  urlFilters: UrlFilters | undefined;
  baseFilters: IncidentListBaseFilters;
  selectedEnvId: string | undefined;
  selectedIdsHash: string;
  enabled: boolean;
};

const useGroupIncidentSummaries = ({
  field,
  groupValues,
  urlFilters,
  baseFilters,
  selectedEnvId,
  selectedIdsHash,
  enabled,
}: Args): Record<string, IncidentListSummary | undefined> => {
  const { accessToken, environmentId, gqlEndpoint } = useGraphqlRequestParams({
    envId: selectedEnvId,
  });

  const queryEnabled =
    enabled && !!field && environmentId !== undefined && !!accessToken;

  const results = useQueries({
    queries: queryEnabled
      ? groupValues.map((groupValue) => ({
          queryKey: [
            INCIDENT_LIST_GROUP_SUMMARY,
            selectedEnvId,
            urlFilters,
            baseFilters,
            selectedIdsHash,
            field,
            groupValue,
          ],
          queryFn: () =>
            request<{ incidentsV2Summary: IncidentListSummary }>(
              gqlEndpoint,
              getIncidentsSummaryQuery,
              {
                filters: baseFilters,
                incidentFilters: [
                  ...(urlFilters || []),
                  {
                    property: field,
                    operator: 'equals',
                    value: [groupValue],
                  },
                ],
              },
              { authorization: `Bearer ${accessToken}` },
            ).then((response) => ({
              groupValue,
              summary: response.incidentsV2Summary,
            })),
          staleTime: Infinity,
        }))
      : [],
  });

  const signature = results
    .map((result) => `${result.dataUpdatedAt}`)
    .join('|');

  return useMemo(() => {
    const summariesByGroup: Record<string, IncidentListSummary | undefined> =
      {};
    results.forEach((result) => {
      if (!result.data) return;
      summariesByGroup[result.data.groupValue] = result.data.summary;
    });
    return summariesByGroup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);
};

export default useGroupIncidentSummaries;
